import React, { useState } from 'react'

import { useActionless, useAppSelector } from 'redux/hooks'
import { selectCategoryList, selectUsedCategories } from 'redux/deck/selectors'

import { CategoryType, generateDefaultCategory } from 'types/deck'
import { SET_DECK_STATE } from 'redux/deck/actions/types'

import CategoryService from 'services/category.service'
import WebsocketService from 'services/websocket.service'
import ToastService from 'services/toast.service'

import CategoryTable from 'components/misc/CategoryTable'
import ConfirmDeleteModal from 'components/elements/ConfirmDeleteModal'
import CategoryModal from 'components/deckPage/categories/Modal'
import CategoryTemplateDeckSettings from 'components/deckPage/settings/forms/Categories/Templates'
import PhatButton from 'components/formElements/PhatButton'

import styles from './categories.module.scss'

const CategorySettings = () => {
  const [createCategoryModalOpen, setCreateCategoryModalOpen] = useState(false)
  const [categoryToBeDeleted, setCategoryToBeDeleted] = useState<CategoryType | null>(null)
  const [categoryToBeEdited, setCategoryToBeEdited] = useState<CategoryType | null>(null)
  const [confirmRemoveUnusuedCategories, setConfirmRemoveUnusuedCategories] = useState(false)

  const categories = useAppSelector(state => state.deck.categories)
  const sortedCategories = useAppSelector(selectCategoryList)
  const usedCategories = useAppSelector(selectUsedCategories)

  const unusedCategories = sortedCategories.filter(category => !usedCategories[category.name])

  const [setDeckState] = useActionless(SET_DECK_STATE)

  const handleDelete = (category: CategoryType) => {
    return CategoryService.remove(category.id)
      .then(() => {
        const newCategories = { ...categories }

        delete newCategories[category.name]

        setDeckState({ categories: newCategories })
        WebsocketService.send({ categories: newCategories }, 'SET_DECK_STATE')
      })
      .catch(() => ToastService.create('Failed to delete category', 'Deck settings', 'error'))
  }

  const handleRemoveUnusedCategories = () => {
    // Note: We're not just calling handleDelete for each category because we want a single state update in redux. Updating them one by one would cause multiple re-renders (and just generally looks bad)
    return Promise.all(unusedCategories.map(category => CategoryService.remove(category.id)))
      .then(() => {
        const newCategories = { ...categories }

        unusedCategories.forEach(category => {
          delete newCategories[category.name]
        })

        setDeckState({ categories: newCategories })
      })
      .catch(() =>
        ToastService.create(
          'Failed to delete one or more categories. Data could be in a weird state since we are not sure which categories were removed. We recommend refreshing the page.',
          'Deck settings',
          'error',
        ),
      )
  }

  return (
    <div className={styles.container}>
      <b>* A category must have no cards in it to be deleted</b>

      <CategoryTable categories={sortedCategories} onDelete={setCategoryToBeDeleted} onEdit={setCategoryToBeEdited} />

      <div className={styles.controls}>
        <PhatButton
          icon="trash alternate outline"
          color="red"
          onClick={() => setConfirmRemoveUnusuedCategories(true)}
          disabled={!unusedCategories.length}>
          Delete unused categories
        </PhatButton>
        <PhatButton icon="add circle" onClick={() => setCreateCategoryModalOpen(true)} color="green">
          Create Category
        </PhatButton>
      </div>

      <CategoryTemplateDeckSettings />

      {/* Modals start here */}

      <ConfirmDeleteModal
        header="Delete category"
        message={`Are you sure you want to delete the category "${categoryToBeDeleted?.name}"?`}
        buttonText={`Delete "${categoryToBeDeleted?.name}"`}
        open={!!categoryToBeDeleted}
        onDelete={() => handleDelete(categoryToBeDeleted || generateDefaultCategory())}
        onClose={() => setCategoryToBeDeleted(null)}
      />

      <ConfirmDeleteModal
        header="Delete empty categories"
        open={confirmRemoveUnusuedCategories}
        onClose={() => setConfirmRemoveUnusuedCategories(false)}
        message={`Are you sure you want to delete all (${unusedCategories.length}) categories that are not being used by any card? This action cannot be undone.`}
        buttonText="Delete unused categories"
        loadingMessage="Deleting categories..."
        onDelete={handleRemoveUnusedCategories}
      />

      <CategoryModal
        category={categoryToBeEdited || generateDefaultCategory()}
        open={!!categoryToBeEdited}
        onClose={() => setCategoryToBeEdited(null)}
        onSave={() => setCategoryToBeEdited(null)}
      />

      <CategoryModal
        open={createCategoryModalOpen}
        onClose={() => setCreateCategoryModalOpen(false)}
        onSave={() => setCreateCategoryModalOpen(false)}
      />
    </div>
  )
}

export default CategorySettings
